import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Switch,
} from '@structural-climbing/ui'
import type { GymPlan } from './types'
import { useGymContext } from '../../contexts/GymContext'

interface PlanFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: PlanFormData) => Promise<void>
  initialValues?: GymPlan
}

export interface PlanFormData {
  name: string
  plan_type: 'day_pass' | 'entry_based' | 'duration_based'
  max_entries: number | null
  duration_days: number
  price: number
  currency: string
  promo_tag: string | null
  payable_online: boolean
  is_addon: boolean
  includes_instructor: boolean
  /** Null for a plan the member pays for. */
  external_provider: string | null
  external_visit_price: number | null
}

function defaultForm(): PlanFormData {
  return {
    name: '',
    plan_type: 'duration_based',
    max_entries: null,
    duration_days: 30,
    price: 0,
    currency: 'RON',
    promo_tag: null,
    payable_online: false,
    is_addon: false,
    includes_instructor: false,
    external_provider: null,
    external_visit_price: null,
  }
}

export function PlanFormModal({ isOpen, onClose, onSave, initialValues }: PlanFormModalProps) {
  const { t } = useTranslation()
  const { paymentsEnabled, slug } = useGymContext()
  const [form, setForm] = useState<PlanFormData>(defaultForm())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [priceStr, setPriceStr] = useState(String(defaultForm().price))
  const [durationStr, setDurationStr] = useState(String(defaultForm().duration_days))
  const [maxEntriesStr, setMaxEntriesStr] = useState('')
  const [isExternal, setIsExternal] = useState(false)
  const [visitPriceStr, setVisitPriceStr] = useState('')

  const PLAN_TYPE_OPTIONS = [
    { key: 'duration_based', label: t('plans.typeDurationBased') },
    { key: 'entry_based', label: t('plans.typeEntryBased') },
    { key: 'day_pass', label: t('plans.typeDayPass') },
  ]

  useEffect(() => {
    if (isOpen) {
      const nextForm = initialValues
        ? {
            name: initialValues.name,
            plan_type: initialValues.plan_type,
            max_entries: initialValues.max_entries,
            duration_days: initialValues.duration_days,
            price: initialValues.price,
            currency: initialValues.currency,
            promo_tag: initialValues.promo_tag,
            payable_online: initialValues.payable_online,
            is_addon: initialValues.is_addon ?? false,
            includes_instructor: initialValues.includes_instructor ?? false,
            external_provider: initialValues.external_provider ?? null,
            external_visit_price:
              initialValues.external_visit_price !== null &&
              initialValues.external_visit_price !== undefined
                ? Number(initialValues.external_visit_price)
                : null,
          }
        : defaultForm()
      setForm(nextForm)
      setPriceStr(String(nextForm.price))
      setDurationStr(String(nextForm.duration_days))
      setMaxEntriesStr(nextForm.max_entries !== null ? String(nextForm.max_entries) : '')
      setIsExternal(!!nextForm.external_provider)
      setVisitPriceStr(
        nextForm.external_visit_price !== null ? String(nextForm.external_visit_price) : '',
      )
      setError(null)
    }
  }, [isOpen, initialValues])

  function set<K extends keyof PlanFormData>(key: K, value: PlanFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleClose() {
    setError(null)
    onClose()
  }

  async function handleSubmit() {
    if (!form.name.trim()) {
      setError(t('plans.nameRequired'))
      return
    }
    if (form.plan_type !== 'day_pass' && (isNaN(form.duration_days) || form.duration_days < 1)) {
      setError(t('plans.durationRequired'))
      return
    }
    if (isExternal) {
      if (!form.external_provider?.trim()) {
        setError(t('plans.externalProviderRequired'))
        return
      }
      if (form.external_visit_price === null || form.external_visit_price < 0) {
        setError(t('plans.externalVisitPriceRequired'))
        return
      }
    }
    setLoading(true)
    setError(null)
    try {
      // The member pays nothing for a partner plan, and it is never sold online.
      await onSave(
        isExternal
          ? {
              ...form,
              external_provider: form.external_provider!.trim(),
              price: 0,
              payable_online: false,
            }
          : { ...form, external_provider: null, external_visit_price: null },
      )
      handleClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('plans.errorSave'))
    } finally {
      setLoading(false)
    }
  }

  const isDayPass = form.plan_type === 'day_pass'
  const isEntryBased = form.plan_type === 'entry_based'

  return (
    <Modal
      scrollBehavior="inside"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>{initialValues ? t('plans.editTitle') : t('plans.newTitle')}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <Input
                  label={t('plans.nameLabel')}
                  placeholder={t('plans.namePlaceholder')}
                  value={form.name}
                  onValueChange={(v) => set('name', v)}
                  autoFocus
                />

                <Select
                  label={t('plans.planTypeLabel')}
                  selectedKeys={[form.plan_type]}
                  onSelectionChange={(keys) => {
                    const val = Array.from(keys)[0] as PlanFormData['plan_type'] | undefined
                    if (!val) return
                    const next = { ...form, plan_type: val }
                    if (val === 'day_pass') next.duration_days = 1
                    if (val !== 'entry_based') next.max_entries = null
                    setForm(next)
                  }}
                >
                  {PLAN_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.key}>{o.label}</SelectItem>
                  ))}
                </Select>

                {/*
                  An add-on is held next to a membership rather than replacing
                  it, and never lets anyone in on its own. It cannot be sold
                  online (gym_plans_addon_not_online), so turning it on clears
                  that flag and hides the toggle below.
                */}
                <div className="flex flex-col gap-1">
                  <Switch
                    isSelected={form.is_addon}
                    onValueChange={(v) => {
                      set('is_addon', v)
                      if (v) set('payable_online', false)
                    }}
                  >
                    <span className="text-sm">{t('plans.addonLabel')}</span>
                  </Switch>
                  <p className="text-xs text-foreground/50">{t('plans.addonHint')}</p>
                </div>

                <div className="flex flex-col gap-1">
                  <Switch
                    isSelected={form.includes_instructor}
                    onValueChange={(v) => set('includes_instructor', v)}
                  >
                    <span className="text-sm">{t('plans.instructorLabel')}</span>
                  </Switch>
                  <p className="text-xs text-foreground/50">{t('plans.instructorHint')}</p>
                </div>

                {/*
                  Paid by a partner (ESX, 7card...): the member shows their pass
                  and pays nothing, the partner pays the gym for each visit. That
                  income gets its own block in Statistics, apart from revenue.
                */}
                <div className="flex flex-col gap-1">
                  <Switch
                    isSelected={isExternal}
                    onValueChange={(v) => {
                      setIsExternal(v)
                      if (v) set('payable_online', false)
                    }}
                  >
                    <span className="text-sm">{t('plans.externalLabel')}</span>
                  </Switch>
                  <p className="text-xs text-foreground/50">{t('plans.externalHint')}</p>
                </div>

                {isExternal && (
                  <Input
                    label={t('plans.externalProviderLabel')}
                    placeholder={t('plans.externalProviderPlaceholder')}
                    value={form.external_provider ?? ''}
                    onValueChange={(v) => set('external_provider', v || null)}
                    maxLength={100}
                  />
                )}

                <div className="grid grid-cols-2 gap-4">
                  {isExternal ? (
                    <Input
                      label={t('plans.externalVisitPriceLabel')}
                      type="number"
                      min="0"
                      step="0.01"
                      value={visitPriceStr}
                      onValueChange={(v) => {
                        setVisitPriceStr(v)
                        const n = parseFloat(v)
                        set('external_visit_price', isNaN(n) ? null : n)
                      }}
                    />
                  ) : (
                    <Input
                      label={t('plans.priceLabel')}
                      type="number"
                      min="0"
                      step="0.01"
                      value={priceStr}
                      onValueChange={(v) => {
                        setPriceStr(v)
                        const n = parseFloat(v)
                        if (!isNaN(n)) set('price', n)
                      }}
                    />
                  )}
                  <Input
                    label={t('plans.currencyLabel')}
                    value={form.currency}
                    onValueChange={(v) => set('currency', v.toUpperCase())}
                    maxLength={3}
                  />
                </div>

                {!isDayPass && (
                  <Input
                    label={t('plans.durationLabel')}
                    type="number"
                    min="1"
                    value={durationStr}
                    onValueChange={(v) => {
                      setDurationStr(v)
                      const n = parseInt(v)
                      set('duration_days', isNaN(n) ? 0 : n)
                    }}
                  />
                )}

                {isEntryBased && (
                  <Input
                    label={t('plans.maxEntriesLabel')}
                    type="number"
                    min="1"
                    value={maxEntriesStr}
                    onValueChange={(v) => {
                      setMaxEntriesStr(v)
                      const n = parseInt(v)
                      set('max_entries', isNaN(n) ? null : n)
                    }}
                    placeholder="e.g. 10"
                  />
                )}

                {/*
                  Any plan but an add-on can be sold online. A plan bought
                  online starts the moment the payment lands, like one sold at
                  the desk.

                  Shown whether or not payments are switched on, so an owner can
                  prepare plans before connecting Stripe, with a hint instead of
                  a hidden control when the gym cannot sell yet.
                */}
                {!form.is_addon && !isExternal && (
                  <div className="flex flex-col gap-1">
                    <Switch
                      isSelected={form.payable_online}
                      onValueChange={(v) => set('payable_online', v)}
                    >
                      <span className="text-sm">{t('plans.payableOnlineLabel')}</span>
                    </Switch>
                    {/*
                      Outside the Switch on purpose: anything inside it sits in
                      the label, so clicking the link would toggle the switch
                      instead of following the link.
                    */}
                    {paymentsEnabled ? (
                      <p className="text-xs text-foreground/50">{t('plans.payableOnlineHint')}</p>
                    ) : (
                      <p className="text-xs text-danger">
                        {t('plans.payableOnlineNeedsPayments')}{' '}
                        <a
                          href={`/${slug}/settings?tab=payments`}
                          className="underline underline-offset-2"
                        >
                          {t('plans.payableOnlineSettingsLink')}
                        </a>
                      </p>
                    )}
                  </div>
                )}

                <Input
                  label={t('plans.promoTagLabel')}
                  placeholder={t('plans.promoTagPlaceholder')}
                  value={form.promo_tag ?? ''}
                  onValueChange={(v) => set('promo_tag', v || null)}
                />

                {error && <p className="text-sm text-danger">{error}</p>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onClick={handleClose} isDisabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button
                color="primary"
                onClick={handleSubmit}
                isLoading={loading}
                isDisabled={!form.name.trim()}
              >
                {initialValues ? t('plans.saveChanges') : t('plans.createPlan')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
